import React, { useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Box,
    useMediaQuery,
    IconButton,
    InputAdornment
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import Spinner from 'components/Spinner';
import axios from '../axios';

const AddAgent = () => {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        contact: ''
    });

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

    const handleClickOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
        setError(''); // Clear any errors when closing
        setShowPassword(false); // Reset password visibility when closing
    };

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = () => {
        setLoading(true);
        setError(''); // Clear previous errors

        const { name, email, password, contact } = formData;

        if (name && email && password && contact) {
            const agentData = {
                ...formData,
                suspended: false
            };

            axios
                .post(`/admin/register-agent`, agentData)
                .then(() => {
                    setLoading(false);
                    // Reset form
                    setFormData({
                        name: '',
                        email: '',
                        password: '',
                        contact: ''
                    });
                    setOpen(false);
                    window.location.href = '/agents';
                })
                .catch((error) => {
                    setError(
                        error.response?.data?.message || 'Failed to add agent. Please try again.'
                    );
                    setLoading(false);
                });
        } else {
            setError('Please fill in all required fields');
            setLoading(false);
        }
    };

    return (
        <div>
            <Button
                variant='contained'
                onClick={handleClickOpen}
                sx={{
                    backgroundColor: '#085d40',
                    color: '#ffffff',
                    fontWeight: 600,
                    padding: '10px 20px',
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontSize: '14px',
                    boxShadow: '0 2px 8px rgba(8, 93, 64, 0.2)',
                    '&:hover': {
                        backgroundColor: '#064532',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(8, 93, 64, 0.3)'
                    },
                    transition: 'all 0.2s ease'
                }}
            >
                Add Agent
            </Button>
            <Dialog
                fullScreen={fullScreen}
                open={open}
                onClose={handleClose}
                aria-labelledby='responsive-dialog-title'
                maxWidth='sm'
                fullWidth
                sx={{
                    '& .MuiDialog-paper': {
                        borderRadius: '16px',
                        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
                        backgroundColor: '#ffffff',
                        margin: '20px',
                        maxHeight: '90vh'
                    }
                }}
            >
                <DialogTitle
                    id='responsive-dialog-title'
                    sx={{
                        textAlign: 'center',
                        padding: '24px 24px 16px 24px',
                        backgroundColor: '#f8fafc',
                        color: '#1f2937',
                        fontSize: '20px',
                        fontWeight: 600,
                        borderBottom: '1px solid #e2e8f0',
                        margin: 0
                    }}
                >
                    Add New Agent
                </DialogTitle>
                <DialogContent
                    sx={{
                        padding: '32px 24px',
                        backgroundColor: '#ffffff'
                    }}
                >
                    <Box
                        component='form'
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 3
                        }}
                        noValidate
                        autoComplete='off'
                    >
                        <TextField
                            required
                            id='agent-name'
                            label='Full Name'
                            name='name'
                            value={formData.name}
                            onChange={handleChange}
                            variant='outlined'
                            fullWidth
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    backgroundColor: '#f9fafb',
                                    '&:hover': {
                                        backgroundColor: '#f3f4f6'
                                    },
                                    '&.Mui-focused': {
                                        backgroundColor: '#ffffff'
                                    }
                                },
                                '& .MuiInputLabel-root': {
                                    color: '#6b7280',
                                    fontWeight: 500
                                },
                                marginTop: '20px'
                            }}
                        />
                        <TextField
                            required
                            id='agent-email'
                            label='Email Address'
                            name='email'
                            value={formData.email}
                            onChange={handleChange}
                            variant='outlined'
                            fullWidth
                            type='email'
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    backgroundColor: '#f9fafb',
                                    '&:hover': {
                                        backgroundColor: '#f3f4f6'
                                    },
                                    '&.Mui-focused': {
                                        backgroundColor: '#ffffff'
                                    }
                                },
                                '& .MuiInputLabel-root': {
                                    color: '#6b7280',
                                    fontWeight: 500
                                }
                            }}
                        />
                        <TextField
                            required
                            id='agent-phone'
                            label='Phone Number'
                            name='contact'
                            value={formData.contact}
                            onChange={handleChange}
                            variant='outlined'
                            fullWidth
                            type='tel'
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    backgroundColor: '#f9fafb',
                                    '&:hover': {
                                        backgroundColor: '#f3f4f6'
                                    },
                                    '&.Mui-focused': {
                                        backgroundColor: '#ffffff'
                                    }
                                },
                                '& .MuiInputLabel-root': {
                                    color: '#6b7280',
                                    fontWeight: 500
                                }
                            }}
                        />
                        <TextField
                            required
                            id='agent-password'
                            label='Password'
                            name='password'
                            type={showPassword ? 'text' : 'password'}
                            value={formData.password}
                            onChange={handleChange}
                            variant='outlined'
                            fullWidth
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position='end'>
                                        <IconButton
                                            aria-label='toggle password visibility'
                                            onClick={handleClickShowPassword}
                                            onMouseDown={handleMouseDownPassword}
                                            edge='end'
                                            sx={{
                                                color: '#6b7280',
                                                '&:hover': {
                                                    color: '#085d40'
                                                }
                                            }}
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    backgroundColor: '#f9fafb',
                                    '&:hover': {
                                        backgroundColor: '#f3f4f6'
                                    },
                                    '&.Mui-focused': {
                                        backgroundColor: '#ffffff'
                                    }
                                },
                                '& .MuiInputLabel-root': {
                                    color: '#6b7280',
                                    fontWeight: 500
                                }
                            }}
                        />
                    </Box>
                    {error && (
                        <Box
                            sx={{
                                marginTop: 2,
                                padding: '12px 16px',
                                backgroundColor: '#fef2f2',
                                border: '1px solid #fecaca',
                                borderRadius: '8px',
                                color: '#dc2626',
                                fontSize: '14px',
                                fontWeight: 500,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}
                        >
                            ⚠️ {error}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions
                    sx={{
                        justifyContent: 'space-between',
                        padding: '20px 24px',
                        backgroundColor: '#f8fafc',
                        borderTop: '1px solid #e2e8f0',
                        gap: 2
                    }}
                >
                    <Button
                        onClick={handleClose}
                        variant='outlined'
                        sx={{
                            borderColor: '#d1d5db',
                            color: '#6b7280',
                            fontWeight: 500,
                            padding: '10px 20px',
                            borderRadius: '8px',
                            textTransform: 'none',
                            minWidth: '100px',
                            '&:hover': {
                                borderColor: '#9ca3af',
                                backgroundColor: '#f9fafb'
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    {loading ? (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                minWidth: '100px',
                                justifyContent: 'center'
                            }}
                        >
                            <Spinner />
                        </Box>
                    ) : (
                        <Button
                            onClick={handleSubmit}
                            variant='contained'
                            sx={{
                                backgroundColor: '#085d40',
                                color: '#ffffff',
                                fontWeight: 600,
                                padding: '10px 20px',
                                borderRadius: '8px',
                                textTransform: 'none',
                                minWidth: '100px',
                                boxShadow: '0 2px 8px rgba(8, 93, 64, 0.2)',
                                '&:hover': {
                                    backgroundColor: '#064532',
                                    transform: 'translateY(-1px)',
                                    boxShadow: '0 4px 12px rgba(8, 93, 64, 0.3)'
                                },
                                transition: 'all 0.2s ease'
                            }}
                        >
                            Add Agent
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default AddAgent;
